import { AnyAction, Dispatch } from 'redux'
import { ContentQuery, ContentService } from '~/services/ContentService'
import { assembleASPExperimentFlags } from 'shared/experiments/ASPSearch/assembleASPExperimentFlags'
import { ASPNewnessExperimentNewnessPercentage } from 'shared/experiments/ASPSearch/ASPNewnessExperiment'
import { getLogger } from 'shared/logger'
import { getContentQueryConfigExperimentFlags } from 'shared/experiments/utils'
import { getGalleryNameSelector, getLocaleSelector, getMpvid, getQuantitySelector, selectedOptionsSelector } from '~/client/store/config'
import { getContentCategories } from '~/client/store/content/actions'
import { taxonomyIdByCategoryIdSelector } from '~/client/store/filterCategories/reducer'
import { refinementIdsByFilterSelector } from '~/client/store/refinement/selectors'
import { MORE_LIKE_THIS_AMOUNT } from '~/client/store/similarTemplates/constants'
import {
  getASPExperimentFlags,
  getBypassApproval,
  getDesignCreationTypes,
  getForcedRankingStrategyOverride,
  getNoCache,
  getRawSets,
  getRenderPropsOverrides,
  getSearchBackend,
  getTemplatePurposes,
  getUseConstraints,
} from '~/client/store/debug/reducer'
import { designPersonalizationContextSelector } from '~/client/store/designPersonalization/selectors'
import {
  SIMILAR_TEMPLATES_ERROR,
  SIMILAR_TEMPLATES_DONE,
  SIMILAR_TEMPLATES_INJECT,
  SIMILAR_TEMPLATES_START,
  SIMILAR_TEMPLATES_RESET,
} from '~/client/store/constants'
import { getRawExperiments } from '~/client/store/experimentation'
import { byId } from '~/client/utils/byId'
import { buildTileEntity } from '~/client/store/buildContentState'
import {
  SimilarTemplatesErrorAction,
  SimilarTemplatesFetchAction,
  SimilarTemplatesFetchParams,
  SimilarTemplatesLoadingAction,
} from '~/client/store/similarTemplates/interface'
import { similarDesignByIdSelector, similarTemplatesByIdSelector } from '~/client/store/similarTemplates/reducer'
import { ThunkDispatch } from '~/client/store/interface'
import { IS_BROWSER } from '~/client/constants'

const similarTemplatesLoading = (loading: boolean): SimilarTemplatesLoadingAction => ({
  type: SIMILAR_TEMPLATES_START,
  payload: loading,
})

const similarTemplatesFetch = ({ designs, tileEntities }: SimilarTemplatesFetchParams): SimilarTemplatesFetchAction => ({
  type: SIMILAR_TEMPLATES_DONE,
  payload: {
    designs,
    tileEntities,
  },
})

const similarTemplatesError = (error: Error): SimilarTemplatesErrorAction => ({
  type: SIMILAR_TEMPLATES_ERROR,
  payload: error,
})

export const fetchSimilarTemplates =
  (designId: string, productOptions: Gallery.ContentQuery.ProductOptions) =>
  async (dispatch: ThunkDispatch, getState: () => State.GlobalState): Promise<AnyAction> => {
    const state = getState()
    const rawExperiments = getRawExperiments(state)
    const experimentFlags = assembleASPExperimentFlags([ASPNewnessExperimentNewnessPercentage], rawExperiments, getASPExperimentFlags(state))
    const configExperimentFlags = getContentQueryConfigExperimentFlags(rawExperiments)
    const refinements = refinementIdsByFilterSelector(state)
    const { categories, facetCategories } = getContentCategories(state)(refinements.categories)

    const designPersonalizationContext = designPersonalizationContextSelector(state)
    const searchBackend = getSearchBackend(state)
    const designCreationTypes = getDesignCreationTypes(state)

    const query: ContentQuery = {
      categories,
      facetCategories,
      designPersonalizationContext,
      bypassApproval: getBypassApproval(state),
      locale: getLocaleSelector(state),
      galleryName: getGalleryNameSelector(state),
      collection: refinements.collection,
      length: MORE_LIKE_THIS_AMOUNT,
      mpvId: getMpvid(state),
      selectedOptions: productOptions || selectedOptionsSelector(state),
      noCache: getNoCache(state),
      useConstraints: getUseConstraints(state),
      forcedRankingStrategyOverride: getForcedRankingStrategyOverride(state),
      renderProps: getRenderPropsOverrides(state),
      templatePurposes: getTemplatePurposes(state),
      searchBackend,
      experimentFlags,
      configExperimentFlags,
      designCreationTypes,
      includeCategoryAndKeywordData: true,
      filterAltTextCompatibleTagging: true,
      moreLike: [designId],
      sets: getRawSets(state),
    }

    dispatch(similarTemplatesLoading(true))
    try {
      const templatesData = await ContentService.getContent(query)
      const getTaxonomyIdByCategoryId = taxonomyIdByCategoryIdSelector(state)
      const productOptionsByProductKeyMap: Gallery.Product.ProductOptionsGroupsByProductKey = {}
      const quantity = getQuantitySelector(state)
      const designs: State.Design[] = []

      const tileEntities = templatesData.results.content.map(
        (tileEntity): State.TileEntity =>
          buildTileEntity({
            tileEntity,
            designs,
            productOptionsByProductKeyMap,
            locale: getLocaleSelector(state),
            quantity,
            getTaxonomyIdByCategoryId,
            isSimilarTemplate: true,
          }),
      )
      const designsById = byId<State.Design>(designs, 'designId')
      const tileEntitiesById = byId<State.TileEntity>(tileEntities, 'designId')

      return dispatch(
        similarTemplatesFetch({
          designs: designsById,
          tileEntities: tileEntitiesById,
        }),
      )
    } catch (e) {
      if (IS_BROWSER) {
        getLogger().error(e as Error)
        dispatch(similarTemplatesError(e as Error))
      }
      throw e
    }
  }

export const similarTemplateInjection =
  (startingEntity: string) =>
  async (dispatch: Dispatch, getState: () => State.GlobalState): Promise<AnyAction> => {
    const state = getState()

    const similarTemplates = similarTemplatesByIdSelector(state)
    const similarDesigns = similarDesignByIdSelector(state)

    const filteredSimilarTemplates = { [startingEntity]: similarTemplates(startingEntity) }
    const colorSwatchesId = filteredSimilarTemplates[startingEntity].colorSwatches

    const filteredSimilarDesigns = Object.fromEntries(colorSwatchesId.filter((id) => similarDesigns(id)).map((id) => [id, similarDesigns(id)]))

    return dispatch({
      type: SIMILAR_TEMPLATES_INJECT,
      payload: {
        designs: {
          byId: filteredSimilarDesigns,
        },
        tileEntities: {
          byId: filteredSimilarTemplates,
        },
      },
    })
  }

export const resetSimilarTemplates = () => ({
  type: SIMILAR_TEMPLATES_RESET,
})
